<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5"
        @fetch="fetchCompanies()"
      >
        <template #start>
          <vs-button
            v-show="isUserCanAccessMenu(permission.name, 'add')"
            class="button shadow-primary font-semibold"
            color="primary"
            @click="addNewOwner"
          >
            Add New Owner
          </vs-button>
          <vs-button
            v-show="true"
            class="button shadow-secondary font-semibold ml-5"
            color="secondary"
            @click="isDownload = true"
          >
            Export
          </vs-button>
        </template>
      </table-top>
      <div class="flex border-b mt-5">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="activeTab = tab.value"
          :class="['px-5 py-3 text-lg font-semibold text-white border-0 rounded-t-lg', activeTab === tab.value ? 'bg-primary border-0 opacity-90' : 'text-gray-500']"
        >
          {{ tab.label }}
        </button>
      </div>
      <data-table
        class="mb-5"
        :loading="isBusy"
        :table-header="filteredTableHeaders"
        :table-data="filteredData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        body-height="auto"
        @sort="sort"
      >
        <template #no="{ index }">
          <span>
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </span>
        </template>
        <template #name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.name.value"
                  :class="{ 'is-not-valid': !row.editForm.name.value }"
                  placeholder="Enter owner name"
                />
              </div>
              <span
                v-if="!row.editForm.name.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.name || '-' }}
          </template>
        </template>
        <template #code="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.code.value"
                  :class="{ 'is-not-valid': !row.editForm.code.value }"
                  placeholder="Enter owner code"
                />
              </div>
              <span
                v-if="!row.editForm.code.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.code || '-' }}
          </template>
        </template>
        <template #site="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <select-with-search
                :disabled="isProcessing"
                v-model="row.editForm.site.value"
                :class="{ 'is-not-valid': !row.editForm.site.isValid && activeTab === 'site_owner' }"
                :options="siteOptions"
                placeholder="Select site"
              />
              <span
                v-if="!row.editForm.site.value && activeTab === 'site_owner'"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.site || '-' }}
          </template>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="Save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed opacity-50 animate-bounce'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="Cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="popUp = { isShown: true, isShownRestore: false, tempId: row.id, tempName: row.name }"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #is_active="{ row }">
          <template v-if="row.isEditing">
            <vs-switch
              :disabled="isProcessing"
              v-model="row.editForm.is_active.value"
              color="secondary"
            />
          </template>
          <template v-else>
            <div
              :class="{
                'bg-green-200 text-green-700 border-green-400': row.is_active,
                'bg-red-200 text-red-700 border-red-400': !row.is_active
              }"
              class="button-l-plan w-20 items-center"
            >
              <span>{{ stateOptions[row.is_active] }}</span>
            </div>
          </template>
        </template>
      </data-table>
      <table-bottom @fetch="fetchCompanies()" />
    </div>
    <pop-up
      is-warning
      title="Restore Owner"
      left-button="Cancel"
      right-button="Restore"
      :active.sync="restorePopup.isShown"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="handleRestore"
    >
      <p class="text-center mt-1">
        {{ `The owner "${restorePopup.tempName}" already exists but has been deleted.` }}
      </p>
      <p class="text-center">
        Would you like to restore it?
      </p>
    </pop-up>
    <pop-up
      is-warning
      title="Delete Owner"
      left-button="Cancel"
      right-button="Yes, Delete"
      :active.sync="popUp.isShown"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteCompany"
    >
      <p class="text-center mt-1">
        Are you sure want to delete "{{ popUp.tempName }}"?
      </p>
      <p class="text-center">
        This action cannot be undone
      </p>
    </pop-up>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'
import downloadUtils from '@/utils/downloadUtils'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    TableBottom,
    TableTop,
    DataTable,
    SelectWithSearch,
    PopUp
  },
  data() {
    return {
      isProcessing: false,
      isBusy: false,
      activeTab: 'tug_owner',
      isAdding: false,
      popUp: {
        isShown: false,
        tempId: null
      },
      restorePopup: {
        isShown: false,
        data: null,
        deletedId: null,
        tempName: null
      },
      cancelPopup: {
        isShown: false,
        row: null
      },
      tabs: [
        { label: 'Tug Owner', value: 'tug_owner' },
        { label: 'Shipper', value: 'site_owner' }
      ],
      options: {
        sites: []
      },
      tableHeaders: [
        { key: 'no', label: 'No', width: '60px' },
        { key: 'name', label: 'Owner Name', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'created_by', label: 'Created By', sortable: true },
        { key: 'created_at', label: 'Created Time', sortable: true }
      ],
      stateOptions: {
        true: 'Active',
        false: 'Inactive'
      },
      companiesData: {
        tug_owner: [],
        site_owner: []
      },
      pagination: {
        total: 0,
        pages: 0
      },
      filters: {
        sort_by: 'name',
        sort_dir: 'asc',
        is_active: null,
        page: 1,
        limit: 10,
        site: []
      },
      editingId: null,
      editForm: {
        name: '',
        code: '',
        is_active: true
      },
      isDownload: false
    }
  },
  async mounted() {
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab
    }
    
    await this.fetchSites();
    await this.fetchCompanies();
  },
  computed: {
    ...mapGetters({
      companies: 'Companies/companies',
      companyDetails: 'Companies/companyDetails',
      totalPage: 'Table/totalPage',
      totalData: 'Table/totalData',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      sites: 'Companies/sites',
      companiesFiles: 'Companies/companiesFiles'
    }),
    restoreMessage() {
      if (!this.restorePopup.data) return 'Owner already exists but has been deleted.'
      return `"${this.restorePopup.data.name}" already exists but has been deleted.`
    },
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    },
    filteredData() {
      return this.companiesData[this.activeTab] || [];
    },
    siteOptions() {
      return this.sites.map(site => ({
        value: site.id,
        text: site.name
      }));
    },
    filteredTableHeaders() {
      const baseHeaders = [
        { key: 'no', label: 'No', width: '60px' },
        { key: 'name', label: 'Owner Name', sortable: true },
        { key: 'code', label: 'Code', sortable: true },
        { key: 'is_active', label: 'Status', sortable: true },
        { key: 'created_by', label: 'Created By', sortable: true },
        { key: 'created_at', label: 'Created Time', sortable: true }
      ];

      if (this.activeTab === 'site_owner') {
        const siteHeader = {
          key: 'site',
          label: 'Site',
          sortable: true
        };
        const codeIndex = baseHeaders.findIndex(header => header.key === 'code');
        baseHeaders.splice(codeIndex + 1, 0, siteHeader);
      }

      if (this.isActionColumnVisible) {
        baseHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
      }

      return baseHeaders;
    }
  },
  watch: {
    activeTab: {
      handler() {
        this.fetchCompanies();
      }
    },
    pageSize: {
      handler(newValue) {
        this.filters.limit = newValue;
        this.filters.page = 1;
        this.fetchCompanies();
      }
    },
    currentPage: {
      handler(newValue) {
        this.filters.page = newValue;
        this.fetchCompanies();
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadCompanies()
        }
      }
    },
    companiesFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          const currentTab = this.tabs.find(tab => tab.value === this.activeTab);
          const filename = currentTab ? `${currentTab.label}.xlsx` : `${this.activeTab}.xlsx`;
          downloadUtils(val, filename)
          // Reset the companiesFiles state
          this.$store.dispatch('Companies/resetCompaniesFiles');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.filteredTableHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.filteredTableHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.filteredTableHeaders.splice(index, 1);
        }
      }
    }
  },
  methods: {
    addNewOwner() {
      if (this.companiesData[this.activeTab].some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first')
        return
      }

      const newRow = {
        id: `new-${Date.now()}`,
        name: '',
        code: '',
        is_active: true,
        created_by: 'SYSTEM',
        created_at: moment().format('DD MMM YYYY HH:mm:ss'),
        site: '-',
        site_id: null,
        isEditing: true,
        editForm: {
          name: { value: '', isValid: true },
          code: { value: '', isValid: true },
          is_active: { value: true, isValid: true },
          site: { value: null, isValid: true }
        }
      };

      this.isAdding = true;
      this.companiesData[this.activeTab].unshift(newRow);
    },
    startEdit(row) {
      if (this.companiesData[this.activeTab].some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first')
        return
      }

      const index = this.companiesData[this.activeTab].findIndex(item => item.id === row.id);
      if (index !== -1) {
        const siteId = this.sites.find(site => site.name === row.site)?.id;

        this.$set(this.companiesData[this.activeTab][index], 'isEditing', true);
        this.$set(this.companiesData[this.activeTab][index], 'editForm', {
          name: { value: row.name, isValid: true },
          code: { value: row.code, isValid: true },
          is_active: { value: row.is_active, isValid: true },
          site: { value: siteId || null, isValid: true }
        });
      }
    },
    async handleRestore() {
      try {
        const dataToRestore = {
          ...this.restorePopup.data,
          restore: true
        };
        this.isProcessing = true
        await this.$store.dispatch('Companies/putCompany', {
          id: this.restorePopup.deletedId,
          form: dataToRestore
        })
        this.showSuccessNotification(this, 'Successfully restored owner')

        this.restorePopup.isShown = false;
        this.restorePopup.data = null;
        this.restorePopup.deletedId = null;
        this.isProcessing = false
        this.isAdding = false;
        this.cancelAllEdits();
        await this.fetchCompanies();
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.message || error?.error || 'Failed to restore owner, please try again')
      }
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(this, 'Some fields is empty, please fill it first')
        return
      }

      try {
        const formData = {
          name: row.editForm.name.value,
          code: row.editForm.code.value,
          type: this.activeTab.toUpperCase(),
          is_active: row.editForm.is_active.value
        };

        if (this.activeTab === 'site_owner') {
          if (!row.editForm.site.value) {
            this.showFailedNotification(this, 'Site is required for Site Owner')
            return
          }
          formData.site_id = row.editForm.site.value
        }

        const duplicateName = this.companiesData[this.activeTab].find(
          company => company.id !== row.id &&
          company.name.toLowerCase() === formData.name.toLowerCase()
        );
        if (duplicateName) {
          this.showFailedNotification(this, 'Owner name already exists')
          return
        }

        const duplicateCode = this.companiesData[this.activeTab].find(
          company => company.id !== row.id &&
          company.code.toLowerCase() === formData.code.toLowerCase()
        );
        if (duplicateCode) {
          this.showFailedNotification(this, 'Code already exists')
          return
        }
        this.isProcessing = true
        const isExist = await this.checkRestoreData(formData);
        if (isExist) {
          this.isProcessing = false
          return
        }

        if (this.isAdding) {
          await this.$store.dispatch('Companies/postCompany', formData)
          this.showSuccessNotification(this, 'Successfully added new owner')
        } else {
          await this.$store.dispatch('Companies/putCompany', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Successfully saved changes')
        }

        this.cancelAllEdits()
        this.fetchCompanies()
        this.isProcessing = false
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.error.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    async checkRestoreData(formData) {
      try {
        const response = await this.$store.dispatch('Companies/getCompanyDetails', {
          name: formData.name
        })
        if (response?.data.is_deleted) {
          this.restorePopup.data = formData;
          this.restorePopup.deletedId = response.data.id;
          this.restorePopup.isShown = true;
          this.restorePopup.tempName = response.data.name;
          return true;
        }
        return false;
      } catch (error) {
        this.showFailedNotification(this, error?.error.message || 'Failed to restore data, please try again')
      }
    },
    cancelEdit(row) {
      if (this.isAdding) {
        const index = this.companiesData[this.activeTab].findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.companiesData[this.activeTab].splice(index, 1)
        }
      } else {
        const index = this.companiesData[this.activeTab].findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.companiesData[this.activeTab][index], 'isEditing', false)
          this.$set(this.companiesData[this.activeTab][index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    cancelAllEdits() {
      this.companiesData[this.activeTab].forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          if (key === 'site') {
            form[key].isValid = this.activeTab === 'site_owner' ? !!form[key].value : true;
          } else if (key === 'is_active') {
            form[key].isValid = true;
          } else {
            form[key].isValid = !!form[key].value;
          }
        }
      });

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      );
    },
    async fetchCompanies() {
      if (this.isBusy) return;

      try {
        this.isAdding = false;
        this.isBusy = true;
        const filters = {
          is_master_data: true,
          type: this.activeTab.toUpperCase(),
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'name',
          site: this.filters.site || [],
          page: this.filters.page || 1,
          limit: this.filters.limit || 10,
          is_active: this.filters.is_active
        };

        await this.$store.dispatch('Companies/fetchCompanies', filters);

        if (this.companies && Array.isArray(this.companies)) {
          const transformedData = this.companies.map(company => {
            const siteName = company.site_id ?
              this.sites.find(site => site.id === company.site_id)?.name || '-' :
              '-';

            return {
              id: company.id,
              name: company.name,
              code: company.code || '-',
              is_active: company.is_active,
              created_by: company.created_by || 'SYSTEM',
              created_at: company.created_at ? moment(company.created_at).format('DD MMM YYYY HH:mm:ss') : '-',
              site: siteName,
              site_id: company.site_id,
              isEditing: false,
              editForm: null
            };
          });

          this.$set(this.companiesData, this.activeTab, transformedData);
        } else {
          this.$set(this.companiesData, this.activeTab, []);
        }
      } catch (error) {
        this.showFailedNotification(this, error?.message || 'Failed to fetch Owner Management list, please try again');
        this.$set(this.companiesData, this.activeTab, []);
      } finally {
        this.isBusy = false;
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchCompanies()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy;
      this.filters.sort_dir = sortDir;
      this.fetchCompanies();
    },
    async fetchSites() {
      try {
        this.isBusy = true
        await this.$store.dispatch('Companies/fetchSites', { is_dropdown: true });
      } catch (error) {
        this.showFailedNotification(this, 'Failed to fetch sites, please try again');
      } finally {
        this.isBusy = false
      }
    },
    async deleteCompany() {
      if (this.companiesData[this.activeTab].some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first')
        return
      }

      try {
        this.isProcessing = true
        await this.$store.dispatch('Companies/deleteCompany', this.popUp.tempId)
        this.showSuccessNotification(this, 'Successfully deleted owner')
        this.popUp.isShown = false
        this.isProcessing = false
        this.popUp.tempId = null
        await this.fetchCompanies()
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.message || error?.error || 'Failed to delete owner, please try again')
      }
    },
    async downloadCompanies() {
      try {
        this.isBusy = true
        await this.$store.dispatch('Companies/downloadCompanies', {
          type: this.activeTab.toUpperCase(),
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'name',
          site: this.filters.site || [],
          is_active: this.filters.is_active
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        });
        this.isDownload = false;
      } catch (error) {
        this.isBusy = false
        this.isDownload = false
        this.showFailedNotification(this, 'Failed to download companies, please try again')
      } finally {
        this.isBusy = false
      }
    },
    closeRestoreModal() {
      this.restorePopup.isShown = false;
      this.restorePopup.data = null;
      this.restorePopup.deletedId = null;
      this.restorePopup.tempName = null;
    }
  }
}
</script>

<style scoped>
.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.text-danger {
  color: #ff647c;
}

.blue-theme {
  background-color: #2D5E9D;
}

.sec{
  background-color: #7451D0;
}
</style>
